import { Icon } from '../../ui/Icon'
import type { Order, OrderStatus } from './api'
import { STATUS_TONE, statusText } from './model'

/**
 * «Собран» у доставки — не этап, а сбой: после сборки заказ сразу ждёт курьера,
 * а «собран» остаётся, только если служба не приняла заявку (R-43). Поэтому на
 * шкале он появляется, лишь когда заказ в нём стоит.
 */
const DELIVERY_FLOW: OrderStatus[] = [
  'new',
  'assembling',
  'awaiting_courier',
  'in_delivery',
  'delivered',
]
const DELIVERY_STUCK: OrderStatus[] = [
  'new',
  'assembling',
  'assembled',
  'awaiting_courier',
  'in_delivery',
  'delivered',
]

const PICKUP_FLOW: OrderStatus[] = ['new', 'assembling', 'ready_for_pickup', 'issued']

/** Статусы, которых на шкале нет: это не этапы пути, а остановки. */
const OFF_FLOW: Record<string, string> = {
  awaiting_payment: 'Ждём оплату',
  payment_expired: 'Оплата просрочена',
  needs_attention: 'Требует решения',
  reservation_expired: 'Бронь истекла',
  cancelled: 'Отменён',
}

type StepState = 'done' | 'now' | 'next'

interface Step {
  key: string
  label: string
  state: StepState
}

/**
 * Путь заказа: пройденные этапы, текущий и те, что впереди. У доставки и
 * самовывоза пути разные, поэтому и шкала разная. Заказ, который выпал из
 * потока, показан отдельной меткой, а не «нулевым» этапом.
 *
 * Цвет — только у текущего этапа и тот же, что у статуса (`STATUS_TONE`):
 * по нему видно, чей сейчас ход. Пройденные — с галочкой, без цвета.
 */
export interface OrderStepperProps {
  order: Order
  /** Подпись под текущим этапом: например, что сообщила служба доставки. */
  sub?: string
  /** Текущий этап ждёт кого-то снаружи — точка дышит. */
  waiting?: boolean
}

export function OrderStepper({ order, sub, waiting }: OrderStepperProps) {
  const stop = OFF_FLOW[order.status]
  const flow =
    order.ship === 'delivery'
      ? order.status === 'assembled'
        ? DELIVERY_STUCK
        : DELIVERY_FLOW
      : PICKUP_FLOW
  const current = flow.indexOf(order.status)
  const steps: Step[] = stop
    ? [
        { key: 'new', label: statusText('new'), state: 'done' },
        { key: order.status, label: stop, state: 'now' },
      ]
    : flow.map((status, i) => ({
        key: status,
        label: statusText(status),
        state: i < current ? 'done' : i === current ? 'now' : 'next',
      }))

  return (
    <ol className="od-steps" aria-label="Путь заказа">
      {steps.map((step) => (
        <li
          key={step.key}
          data-state={step.state}
          data-tone={step.state === 'now' ? STATUS_TONE[order.status] : undefined}
          data-waiting={(step.state === 'now' && waiting) || undefined}
          aria-current={step.state === 'now' ? 'step' : undefined}
        >
          <span className="dot" aria-hidden="true">
            {step.state === 'done' ? <Icon name="i-check" size={12} /> : null}
          </span>
          {step.label}
          {step.state === 'now' && sub ? <span className="sub">{sub}</span> : null}
        </li>
      ))}
    </ol>
  )
}
